// Markdown 渲染器 / Markdown renderer — sanitizes frontmatter, raw HTML, and script injection before DOM insert.
// 仅供 Web UI 渲染受控 Markdown（来自后端或本地生成），不适合渲染不受信任的用户输入。
// Only intended for rendering controlled Markdown (from backend or local generation), not untrusted user input.

import { marked } from "marked";

// 去除 YAML frontmatter 块 / Strip the leading YAML frontmatter block.
export function stripMarkdownFrontmatter(source: string): string {
  return source.replace(/^---[ \t]*\n[\s\S]*?\n---[ \t]*(?:\n|$)/, "");
}

// 将原始 HTML 标签逐一转义 / Escape raw HTML-like tags so they render as text.
export function stripRawHtmlBlocks(source: string): string {
  return source.replace(/<[^>\n]+>/g, (match) =>
    match
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;"),
  );
}

interface RenderOptions {
  stripFrontmatter?: boolean;
}

// 将 Markdown 渲染为安全 HTML / Render Markdown to sanitized HTML suitable for innerHTML.
export function renderMarkdownToSafeHtml(
  markdown: string,
  options: RenderOptions = {},
): string {
  const source = options.stripFrontmatter
    ? stripMarkdownFrontmatter(markdown)
    : String(markdown || "");
  const safe = stripRawHtmlBlocks(source);
  const html = marked.parse(
    safe,
    { mangle: false, headerIds: false } as unknown as Parameters<typeof marked.parse>[1],
  ) as string;
  return sanitizeRenderedHtml(html);
}

// 对渲染结果做 DOM 级清理 / Post-render DOM-level sanitization: drop dangerous elements / attributes.
function sanitizeRenderedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  doc.querySelectorAll(
    "script, iframe, object, embed, form, input, textarea, select, button",
  ).forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((node) => {
    for (const attr of [...node.attributes]) {
      const name = attr.name.toLowerCase();
      const normalizedValue = attr.value
        .replace(/[\u0000-\u001f\u007f-\u009f\s]+/g, "")
        .trim()
        .toLowerCase();
      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
        continue;
      }
      if (
        (name === "href" || name === "src" || name === "xlink:href" || name === "action" || name === "formaction") &&
        normalizedValue.startsWith("javascript:")
      ) {
        node.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}
